import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mountain, Home, Compass, Calendar, Plus, Settings, LogOut, 
  BarChart3, CreditCard, Package, Users, TrendingUp, Star,
  ChevronRight, Bell, Check, X, MapPin, Instagram, Eye, Edit, Trash2, ExternalLink,
  Search, Loader2, Image, Building, Clock, CheckCircle, XCircle, Phone, Wallet, IndianRupee, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PLANS = {
  basic: {
    name: 'Basic',
    monthly_price: 0,
    annual_price: 0,
    commission_rate: 10,
    max_listings: 1,
    features: ['1 Listing', '5 Photos/listing', 'Basic Analytics', 'Email Support']
  },
  professional: {
    name: 'Professional',
    monthly_price: 999,
    annual_price: 9990,
    commission_rate: 8,
    max_listings: 5,
    features: ['5 Listings', '15 Photos/listing', 'Video Upload', 'Priority Search', 'WhatsApp Notifications', 'Advanced Analytics']
  },
  enterprise: {
    name: 'Enterprise',
    monthly_price: 1999,
    annual_price: 19990,
    commission_rate: 5,
    max_listings: -1,
    features: ['Unlimited Listings', 'Unlimited Photos & Videos', 'Verified Badge', 'Top Search Ranking', 'SMS Alerts', 'OTA Sync', 'Dedicated Manager']
  }
};

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [listings, setListings] = useState({ stays: [], treks: [], events: [] });
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [activeTab, setActiveTab] = useState('stays');
  const [activeSection, setActiveSection] = useState('listings'); // 'listings' | 'earnings'
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [importType, setImportType] = useState(null);
  const [googleSearchQuery, setGoogleSearchQuery] = useState('');
  const [googleSearchResults, setGoogleSearchResults] = useState([]);
  const [instagramHandle, setInstagramHandle] = useState('');
  const [upiId, setUpiId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [searchingGoogle, setSearchingGoogle] = useState(false);

  useEffect(() => {
    fetchSupplierData();
    fetchListings();
    fetchBookings();
    fetchEarnings();
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('supplier_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchSupplierData = async () => {
    try {
      const [profileRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/supplier/auth/me`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/supplier/analytics/overview`, { headers: getAuthHeaders() })
      ]);

      if (!profileRes.ok) {
        navigate('/supplier/onboarding');
        return;
      }

      const profileData = await profileRes.json();
      setSupplier(profileData);

      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  };

  const fetchListings = async () => {
    try {
      const [staysRes, treksRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/supplier/listings/stays`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/supplier/listings/treks`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/supplier/listings/events`, { headers: getAuthHeaders() })
      ]);

      const staysData = staysRes.ok ? await staysRes.json() : { listings: [] };
      const treksData = treksRes.ok ? await treksRes.json() : { listings: [] };
      const eventsData = eventsRes.ok ? await eventsRes.json() : { listings: [] };

      setListings({
        stays: staysData.listings || [],
        treks: treksData.listings || [],
        events: eventsData.listings || []
      });
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/bookings`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/earnings`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEarnings(data);
        setUpiId(data.payout_method?.upi_id || '');
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const handleRequestPayout = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/earnings/request-payout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ upi_id: upiId })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setShowPayoutModal(false);
        fetchEarnings();
      } else {
        toast.error(data.detail || 'Failed to request payout');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/bookings/${bookingId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success(`Booking ${action}d successfully`);
        fetchBookings();
      } else {
        toast.error(`Failed to ${action} booking`);
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const searchGooglePlaces = async () => {
    if (!googleSearchQuery.trim()) return;
    setSearchingGoogle(true);
    try {
      const response = await fetch(
        `${API_URL}/api/supplier/google-places/search?query=${encodeURIComponent(googleSearchQuery)}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      setGoogleSearchResults(data.results || []);
    } catch (error) {
      toast.error('Failed to search Google Places');
    }
    setSearchingGoogle(false);
  };

  const importFromGoogle = async (placeId) => {
    try {
      const response = await fetch(`${API_URL}/api/supplier/google-places/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ place_id: placeId })
      });
      if (response.ok) {
        toast.success('Business details imported successfully!');
        setShowImportModal(false);
        fetchSupplierData();
      } else {
        toast.error('Failed to import business details');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const connectInstagram = async () => {
    if (!instagramHandle.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/supplier/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ instagram_handle: instagramHandle.replace('@', '') })
      });
      if (response.ok) {
        toast.success('Instagram connected successfully!');
        setShowImportModal(false);
        fetchSupplierData();
      }
    } catch (error) {
      toast.error('Failed to connect Instagram');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/supplier/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (error) {}
    
    localStorage.removeItem('supplier_token');
    localStorage.removeItem('supplier');
    navigate('/supplier/onboarding');
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    
    // If Basic plan (free), activate directly without payment
    if (plan === 'basic') {
      try {
        const response = await fetch(`${API_URL}/api/supplier/subscription/activate-free`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          toast.success('Basic plan activated!');
          setShowSubscriptionModal(false);
          fetchSupplierData();
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Failed to activate plan');
        }
      } catch (error) {
        toast.error('Network error. Please try again.');
      }
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/supplier/subscription/select`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          plan: plan,
          billing: billingCycle
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Initialize Razorpay
        const options = {
          key: data.key_id,
          amount: data.amount * 100,
          currency: data.currency,
          name: 'GoHimalayan',
          description: `${SUBSCRIPTION_PLANS[plan].name} Plan - ${billingCycle === 'annual' ? 'Annual' : 'Monthly'}`,
          order_id: data.order_id,
          handler: async (response) => {
            // Verify payment
            const verifyRes = await fetch(`${API_URL}/api/supplier/subscription/verify-payment`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            if (verifyRes.ok) {
              toast.success('Subscription activated!');
              setShowSubscriptionModal(false);
              fetchSupplierData();
            } else {
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            contact: supplier?.phone
          },
          theme: {
            color: '#2C5F8D'
          }
        };

        if (window.Razorpay) {
          const rzp = new window.Razorpay(options);
          rzp.open();
        } else {
          toast.error('Payment gateway not loaded. Please refresh and try again.');
        }
      } else {
        toast.error(data.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
  };

  const handleDeleteListing = async (listingId, type) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      const endpoint = type === 'stay' ? 'stays' : type === 'trek' ? 'treks' : 'events';
      const response = await fetch(`${API_URL}/api/supplier/listings/${endpoint}/${listingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Listing deleted successfully');
        fetchListings();
      } else {
        toast.error('Failed to delete listing');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  // Listings Grid Component
  const ListingsGrid = ({ listings, type, emptyMessage, onAddNew, isActive, onDelete }) => {
    if (listings.length === 0) {
      return (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">{emptyMessage}</p>
          {isActive && (
            <Button onClick={onAddNew} className="bg-[#2C5F8D] hover:bg-[#1e4570]">
              <Plus className="w-4 h-4 mr-2" />
              Add {type === 'stay' ? 'Property' : type === 'trek' ? 'Trek' : 'Event'}
            </Button>
          )}
        </div>
      );
    }

    const getStatusBadge = (status) => {
      const styles = {
        active: 'bg-green-100 text-green-700',
        pending_review: 'bg-yellow-100 text-yellow-700',
        paused: 'bg-gray-100 text-gray-700',
        draft: 'bg-blue-100 text-blue-700'
      };
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
          {status?.replace('_', ' ') || 'Draft'}
        </span>
      );
    };

    return (
      <div className="grid gap-4">
        {listings.map((listing) => (
          <div key={listing.listing_id} className="flex items-center gap-4 p-4 border rounded-xl hover:bg-gray-50 transition-all">
            {/* Image */}
            <div className="w-20 h-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
              {listing.images && listing.images[0] ? (
                <img 
                  src={listing.images[0]} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-gray-900 truncate">{listing.title}</h4>
                {getStatusBadge(listing.status)}
              </div>
              <p className="text-sm text-gray-500 truncate">
                {type === 'stay' && (
                  <>
                    {listing.location?.city}, {listing.location?.state} • ₹{listing.price_per_night?.toLocaleString()}/night
                  </>
                )}
                {type === 'trek' && (
                  <>
                    {listing.duration_days} days • {listing.difficulty} • ₹{listing.price_per_person?.toLocaleString()}/person
                  </>
                )}
                {type === 'event' && (
                  <>
                    {listing.venue?.city} • {new Date(listing.start_date).toLocaleDateString()}
                  </>
                )}
              </p>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {listing.views || 0}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {listing.bookings_count || 0}</span>
                {listing.rating > 0 && (
                  <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {listing.rating.toFixed(1)}</span>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button 
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                title="Edit"
                data-testid={`edit-${listing.listing_id}`}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button 
                onClick={() => onDelete(listing.listing_id, type)}
                className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                title="Delete"
                data-testid={`delete-${listing.listing_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        
        {isActive && (
          <button
            onClick={onAddNew}
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-[#2C5F8D] hover:text-[#2C5F8D] transition-all"
          >
            <Plus className="w-5 h-5" />
            Add another {type === 'stay' ? 'property' : type === 'trek' ? 'trek' : 'event'}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C5F8D]"></div>
      </div>
    );
  }

  const plan = SUBSCRIPTION_PLANS[supplier?.subscription_plan || 'basic'];
  const isActive = supplier?.subscription_status === 'active';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <div>
                <h1 className="text-lg font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                  GoHimalayan
                </h1>
                <p className="text-xs text-gray-500">Partner Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-500" />
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Activation Banner for Inactive Subscription */}
        {!isActive && (
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold mb-1">Start Listing for FREE!</h2>
                <p className="text-green-100">
                  Activate your free Basic plan to start listing your property, trek, or event.
                </p>
              </div>
              <Button 
                onClick={() => handleSelectPlan('basic')}
                className="bg-white text-green-600 hover:bg-green-50 font-semibold"
                data-testid="activate-free-btn"
              >
                Activate Free Plan
              </Button>
            </div>
          </div>
        )}

        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#2C5F8D] to-[#1e4570] rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">Welcome, {supplier?.business_name || 'Partner'}!</h2>
              <p className="text-blue-100">
                {isActive 
                  ? `${plan.name} Plan • ${plan.commission_rate}% Commission`
                  : 'Activate your subscription to start listing'
                }
              </p>
            </div>
            <Button 
              onClick={() => setShowSubscriptionModal(true)}
              className="bg-white text-[#2C5F8D] hover:bg-blue-50"
              data-testid="choose-plan-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {isActive ? 'Upgrade Plan' : 'Choose Plan'}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-[#2C5F8D]" />
              </div>
              <span className="text-sm text-gray-500">Total Listings</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics?.listings?.total || 0}</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Bookings</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{analytics?.bookings?.total || 0}</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-500">Total Revenue</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">₹{earnings?.summary?.total_booking_value?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm cursor-pointer hover:bg-green-50 transition-all" onClick={() => setShowPayoutModal(true)}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Pending Payout</span>
            </div>
            <div className="text-2xl font-bold text-green-600">₹{earnings?.summary?.pending_payout?.toLocaleString() || 0}</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500">Rating</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{supplier?.rating?.toFixed(1) || '-'}</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {supplier?.supplier_types?.includes('host') && (
              <button 
                onClick={() => {
                  if (isActive) {
                    navigate('/supplier/listings/stays/new');
                  } else {
                    setShowSubscriptionModal(true);
                  }
                }}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#2C5F8D] hover:bg-blue-50 transition-all"
                data-testid="add-stay-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-[#2C5F8D]" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-gray-900">Add Stay</div>
                  <div className="text-sm text-gray-500">{isActive ? 'List a property' : 'Activate plan first'}</div>
                </div>
                <Plus className="w-5 h-5 text-gray-400 ml-auto" />
              </button>
            )}
            
            {supplier?.supplier_types?.includes('trek_organizer') && (
              <button 
                onClick={() => {
                  if (isActive) {
                    navigate('/supplier/listings/treks/new');
                  } else {
                    setShowSubscriptionModal(true);
                  }
                }}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#E67E22] hover:bg-orange-50 transition-all"
                data-testid="add-trek-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Compass className="w-5 h-5 text-[#E67E22]" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-gray-900">Add Trek</div>
                  <div className="text-sm text-gray-500">{isActive ? 'Create trek listing' : 'Activate plan first'}</div>
                </div>
                <Plus className="w-5 h-5 text-gray-400 ml-auto" />
              </button>
            )}
            
            {supplier?.supplier_types?.includes('event_organizer') && (
              <button 
                onClick={() => {
                  if (isActive) {
                    navigate('/supplier/listings/events/new');
                  } else {
                    setShowSubscriptionModal(true);
                  }
                }}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#DEB1FF] hover:bg-[#DEB1FF]/10 transition-all"
                data-testid="add-event-btn"
              >
                <div className="w-10 h-10 rounded-lg bg-[#DEB1FF]/30 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#B388EB]" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-gray-900">Add Event</div>
                  <div className="text-sm text-gray-500">{isActive ? 'Create event listing' : 'Activate plan first'}</div>
                </div>
                <Plus className="w-5 h-5 text-gray-400 ml-auto" />
              </button>
            )}
          </div>
        </div>

        {/* Incoming Bookings Section */}
        {isActive && bookings.filter(b => b.status === 'pending').length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8 border-2 border-yellow-200">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900">Booking Requests</h3>
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                {bookings.filter(b => b.status === 'pending').length} pending
              </span>
            </div>
            
            <div className="space-y-4">
              {bookings.filter(b => b.status === 'pending').map(booking => (
                <div key={booking.booking_id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border rounded-xl bg-yellow-50">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{booking.listing?.title || 'Listing'}</div>
                    <div className="text-sm text-gray-600">
                      {booking.guest_name} • {booking.guests} guests
                    </div>
                    <div className="text-sm text-gray-500">
                      {booking.check_in} → {booking.check_out}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{booking.guest_phone}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">₹{booking.total_amount?.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">{booking.nights} nights</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleBookingAction(booking.booking_id, 'approve')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBookingAction(booking.booking_id, 'decline')}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Import Data Section */}
        {isActive && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Your Business</h3>
            <p className="text-sm text-gray-500 mb-4">
              Speed up your onboarding by importing existing business data
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                onClick={() => { setImportType('google'); setShowImportModal(true); }}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 transition-all"
                data-testid="import-google-btn"
              >
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-red-500" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-gray-900">Google Maps</div>
                  <div className="text-sm text-gray-500">Import from Google Business</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
              </button>
              
              <button
                onClick={() => { setImportType('instagram'); setShowImportModal(true); }}
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-pink-400 hover:bg-pink-50 transition-all"
                data-testid="import-instagram-btn"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-pink-500" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-gray-900">Instagram</div>
                  <div className="text-sm text-gray-500">Connect your Instagram page</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
              </button>
            </div>
          </div>
        )}

        {/* My Listings Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">My Listings</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('stays')}
                className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                  activeTab === 'stays' ? 'bg-[#2C5F8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Stays ({listings.stays.length})
              </button>
              <button
                onClick={() => setActiveTab('treks')}
                className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                  activeTab === 'treks' ? 'bg-[#E67E22] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Treks ({listings.treks.length})
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                  activeTab === 'events' ? 'bg-[#DEB1FF] text-gray-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Events ({listings.events.length})
              </button>
            </div>
          </div>

          {/* Listings Content */}
          {activeTab === 'stays' && (
            <ListingsGrid 
              listings={listings.stays} 
              type="stay" 
              emptyMessage="No stays listed yet"
              onAddNew={() => navigate('/supplier/listings/stays/new')}
              isActive={isActive}
              onDelete={handleDeleteListing}
            />
          )}
          {activeTab === 'treks' && (
            <ListingsGrid 
              listings={listings.treks} 
              type="trek" 
              emptyMessage="No treks listed yet"
              onAddNew={() => navigate('/supplier/listings/treks/new')}
              isActive={isActive}
              onDelete={handleDeleteListing}
            />
          )}
          {activeTab === 'events' && (
            <ListingsGrid 
              listings={listings.events} 
              type="event" 
              emptyMessage="No events listed yet"
              onAddNew={() => navigate('/supplier/listings/events/new')}
              isActive={isActive}
              onDelete={handleDeleteListing}
            />
          )}
        </div>

        {/* Subscription Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Your Plan</h3>
            <button 
              onClick={() => setShowSubscriptionModal(true)}
              className="text-sm text-[#2C5F8D] hover:underline"
            >
              {isActive ? 'Upgrade' : 'Choose Plan'}
            </button>
          </div>
          
          <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isActive ? 'bg-green-100' : 'bg-gray-200'
            }`}>
              <CreditCard className={`w-6 h-6 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">{plan.name} Plan</div>
              <div className="text-sm text-gray-500">
                {isActive 
                  ? `Renews on ${new Date(supplier?.subscription_end).toLocaleDateString()}`
                  : 'Not active'
                }
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isActive 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-200 text-gray-600'
            }`}>
              {isActive ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Choose Your Plan</h2>
              <button 
                onClick={() => setShowSubscriptionModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Billing Toggle */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-center gap-4">
                <span className={`text-sm ${billingCycle === 'monthly' ? 'font-semibold' : 'text-gray-500'}`}>Monthly</span>
                <button
                  onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
                  className={`w-14 h-7 rounded-full p-1 transition-colors ${
                    billingCycle === 'annual' ? 'bg-[#2C5F8D]' : 'bg-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-7' : 'translate-x-0'
                  }`} />
                </button>
                <span className={`text-sm ${billingCycle === 'annual' ? 'font-semibold' : 'text-gray-500'}`}>
                  Annual <span className="text-green-600">(2 months free)</span>
                </span>
              </div>
            </div>
            
            {/* Plans */}
            <div className="p-6 grid md:grid-cols-3 gap-4">
              {Object.entries(SUBSCRIPTION_PLANS).map(([key, planData]) => (
                <div 
                  key={key}
                  className={`rounded-xl border-2 p-6 ${
                    key === 'professional' 
                      ? 'border-[#2C5F8D] ring-2 ring-[#2C5F8D]/20' 
                      : 'border-gray-200'
                  }`}
                >
                  {key === 'professional' && (
                    <div className="text-xs font-semibold text-[#2C5F8D] mb-2">MOST POPULAR</div>
                  )}
                  {key === 'basic' && (
                    <div className="text-xs font-semibold text-green-600 mb-2">FREE FOREVER</div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{planData.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    {planData.monthly_price === 0 ? (
                      <span className="text-3xl font-bold text-green-600">FREE</span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold">
                          ₹{billingCycle === 'annual' ? planData.annual_price : planData.monthly_price}
                        </span>
                        <span className="text-gray-500">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm text-orange-600 font-medium mb-4">
                    {planData.commission_rate}% commission per booking
                  </div>
                  <ul className="space-y-2 mb-6">
                    {planData.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSelectPlan(key)}
                    className={`w-full ${
                      key === 'professional'
                        ? 'bg-[#2C5F8D] hover:bg-[#1e4570]'
                        : key === 'basic'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                    disabled={supplier?.subscription_plan === key && isActive}
                  >
                    {supplier?.subscription_plan === key && isActive 
                      ? 'Current Plan' 
                      : key === 'basic' 
                      ? 'Start Free' 
                      : 'Select Plan'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {importType === 'google' ? 'Import from Google Maps' : 'Connect Instagram'}
              </h2>
              <button 
                onClick={() => { setShowImportModal(false); setGoogleSearchResults([]); setGoogleSearchQuery(''); setInstagramHandle(''); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {importType === 'google' ? (
                <>
                  <p className="text-gray-600 mb-4">
                    Search for your business on Google Maps to import photos, address, and reviews.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={googleSearchQuery}
                      onChange={(e) => setGoogleSearchQuery(e.target.value)}
                      placeholder="Search your business name..."
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && searchGooglePlaces()}
                      data-testid="google-search-input"
                    />
                    <Button 
                      onClick={searchGooglePlaces}
                      disabled={searchingGoogle || !googleSearchQuery.trim()}
                      className="bg-[#2C5F8D]"
                    >
                      {searchingGoogle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>

                  {/* Search Results */}
                  {googleSearchResults.length > 0 && (
                    <div className="space-y-3">
                      {googleSearchResults.map((place) => (
                        <div 
                          key={place.place_id}
                          className="flex items-start gap-3 p-4 border rounded-xl hover:bg-gray-50 cursor-pointer"
                          onClick={() => importFromGoogle(place.place_id)}
                          data-testid={`place-${place.place_id}`}
                        >
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                            {place.photos && place.photos[0] ? (
                              <img src={place.photos[0]} alt={place.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building className="w-6 h-6 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{place.name}</h4>
                            <p className="text-sm text-gray-500">{place.address}</p>
                            {place.rating && (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center gap-1 text-yellow-500">
                                  <Star className="w-3 h-3 fill-current" />
                                  <span className="text-xs">{place.rating}</span>
                                </div>
                                <span className="text-xs text-gray-400">({place.review_count} reviews)</span>
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 mt-2" />
                        </div>
                      ))}
                    </div>
                  )}

                  {googleSearchQuery && googleSearchResults.length === 0 && !searchingGoogle && (
                    <p className="text-center text-gray-500 py-8">No results found. Try a different search term.</p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Connect your Instagram business page to import photos and showcase your social presence.
                  </p>
                  <div className="relative mb-4">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                    <Input
                      value={instagramHandle}
                      onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                      placeholder="your_business_handle"
                      className="pl-8"
                      data-testid="instagram-input"
                    />
                  </div>
                  <Button 
                    onClick={connectInstagram}
                    disabled={!instagramHandle.trim()}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    Connect Instagram
                  </Button>
                  
                  {supplier?.instagram_handle && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg flex items-center gap-2 text-green-700">
                      <Check className="w-5 h-5" />
                      <span>Connected: @{supplier.instagram_handle}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Request Payout</h2>
              <button 
                onClick={() => setShowPayoutModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Earnings Summary */}
              <div className="bg-green-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Total Earnings</span>
                  <span className="font-semibold">₹{earnings?.summary?.total_earnings?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Commission ({earnings?.summary?.commission_rate || 10}%)</span>
                  <span className="text-orange-600">-₹{earnings?.summary?.total_commission_paid?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Already Paid</span>
                  <span className="text-gray-500">-₹{earnings?.summary?.total_paid_out?.toLocaleString() || 0}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">Available for Payout</span>
                    <span className="text-xl font-bold text-green-600">₹{earnings?.summary?.pending_payout?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* UPI Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your UPI ID
                </label>
                <Input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="yourname@upi or 9876543210@paytm"
                  className="w-full"
                  data-testid="upi-input"
                />
                <p className="text-xs text-gray-500 mt-1">We'll send your payout to this UPI ID</p>
              </div>
              
              {/* WhatsApp Info */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Payout via WhatsApp</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Our team will process your payout within 24-48 hours and confirm via WhatsApp at your registered number.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      <strong>GoHimalayan WhatsApp:</strong> +91 9871155963
                    </p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleRequestPayout}
                disabled={!upiId.trim() || (earnings?.summary?.pending_payout || 0) <= 0}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="request-payout-btn"
              >
                <IndianRupee className="w-4 h-4 mr-2" />
                Request Payout of ₹{earnings?.summary?.pending_payout?.toLocaleString() || 0}
              </Button>
              
              {(earnings?.summary?.pending_payout || 0) <= 0 && (
                <p className="text-center text-sm text-gray-500 mt-3">
                  No pending payouts available
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierDashboard;
